import React, { useState, useMemo } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { emailLogsMockData } from '@/lib/constants';
import { useNavigate } from 'react-router-dom';
import TicketModal from 'components/tickets/TicketModal';

interface EmailLog {
  emailType: string;
  status: string;
  sendTo: string;
  date: string;
  subject?: string;
  content?: string;
  organization: string;
}

interface EmailLogsProps {
  filters: {
    emailType: string;
    status: string;
    sendTo: string;
    date: string;
    search: string;
    statuses?: string[];
    startDate: Date | null;
    endDate: Date | null;
    organization: string;
  };
  setFilters: (filters: any) => void;
}

export default function EmailLogs({ filters, setFilters }: EmailLogsProps) {
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedLogForTicket, setSelectedLogForTicket] = useState<EmailLog | null>(null);
  const [emails, setEmails] = useState<EmailLog[]>(emailLogsMockData);

  const filteredEmails = useMemo(() => {
    let filtered = [...emails];

    // Filter by statuses (multiple statuses can be selected)
    if (filters.statuses && filters.statuses.length > 0) {
      filtered = filtered.filter(email =>
        filters.statuses!.includes(email.status.toLowerCase())
      );
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(email =>
        email.emailType.toLowerCase().includes(searchTerm) ||
        email.status.toLowerCase().includes(searchTerm) ||
        email.sendTo.toLowerCase().includes(searchTerm) ||
        email.date.toLowerCase().includes(searchTerm) ||
        email.organization.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by email type
    if (filters.emailType) {
      filtered = filtered.filter(email =>
        email.emailType === filters.emailType
      );
    }

    // Filter by status (single status)
    if (filters.status) {
      filtered = filtered.filter(email =>
        email.status === filters.status
      );
    }

    // Filter by send to
    if (filters.sendTo) {
      filtered = filtered.filter(email =>
        email.sendTo.toLowerCase().includes(filters.sendTo.toLowerCase())
      );
    }

    // Filter by organization
    if (filters.organization) {
      filtered = filtered.filter(email =>
        email.organization.toLowerCase().includes(filters.organization.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(email => {
        const emailDate = new Date(email.date);
        // Set time to start of day for start date and end of day for end date
        const startDate = new Date(filters.startDate as Date);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(filters.endDate as Date);
        endDate.setHours(23, 59, 59, 999);

        return emailDate >= startDate && emailDate <= endDate;
      });
    }

    return filtered;
  }, [emails, filters]);

  const navigate = useNavigate();

  const DetailRow = ({ label, value, className = '' }: { label: string; value: string; className?: string }) => (
    <div className={`flex border-b border-white/[0.08] py-3 ${className}`}>
      <div className="w-1/4 text-gray-400">{label}</div>
      <div className="w-2/4 text-white break-words whitespace-pre-wrap">{value}</div>
    </div>
  );

  const handleReport = (log: EmailLog) => {
    setSelectedLogForTicket(log);
    setIsTicketModalOpen(true);
  };

  return (
    <>

      <div className="sticky top-[130px] bg-black z-10 pt-4">
        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Organization</th>
              <th className="text-left p-4">Email Type</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Send To</th>
            </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full  table-fixed">
          <tbody>
            {filteredEmails.map((log, index) => (
              <React.Fragment key={index}>
                <tr
                  className="border-b border-white/[0.08] hover:bg-[#3a3a3a33] cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === index ? null : index)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {expandedLog === index ? (
                        <FiChevronDown className="text-gray-400" />
                      ) : (
                        <FiChevronRight className="text-gray-400" />
                      )}
                      <span className="text-gray-400">{new Date(log.date).toLocaleDateString()} </span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-400">{log.organization}</td>
                  <td className="p-4">
                    <span className="text-white">{log.emailType}</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${log.status === 'Sent' ? 'bg-[#1C3829] text-[#4ADE80]' :
                      log.status === 'Failed' ? 'bg-[#e11d486b] text-red-500' :
                        'bg-yellow-900/30 text-yellow-500'
                      }`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="p-4 text-gray-400">{log.sendTo}</td>
                </tr>

                {expandedLog === index && (
                  <tr>
                    <td colSpan={5} className="bg-[#1a1a1a] border-b border-gray-800/50 w-full">
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-lg text-white font-medium">Email Details</h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReport(log);
                            }}
                            className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white hover:bg-[#3a3a3a33]"
                          >
                            Report
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <DetailRow label="Date" value={new Date(log.date).toLocaleDateString()} />
                            <DetailRow label="Email Type" value={log.emailType} />
                            <DetailRow label="Send To" value={log.sendTo} />
                            <DetailRow label="Organization" value={log.organization} />
                          </div>
                          <div className="space-y-3">
                            <DetailRow label="Status" value={log.status} />
                            <DetailRow label="Subject" value={log?.subject || 'N/A'} />
                            <DetailRow label="Content" value={log?.content || 'N/A'} />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <TicketModal
        isOpen={isTicketModalOpen}
        onClose={() => {
          setIsTicketModalOpen(false);
          setSelectedLogForTicket(null);
        }}
        mode="create"
        initialData={selectedLogForTicket ? {
          id: '',
          status: 'open',
          customer: selectedLogForTicket.sendTo,
          name: `Report: Email Issue - ${selectedLogForTicket.emailType}`,
          description: `Reporting email issue:\n\nDate: ${selectedLogForTicket.date}\nType: ${selectedLogForTicket.emailType}\nStatus: ${selectedLogForTicket.status}\nRecipient: ${selectedLogForTicket.sendTo}`,
          addedOn: new Date().toISOString(),
          assignedTo: '',
          lastUpdated: new Date().toISOString(),
          email: selectedLogForTicket.sendTo
        } : null}
      />
    </>
  );
} 